import { Button } from "@shared-ui/button";
import { Input } from "@shared-ui/input";
import { Textarea } from "@shared-ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { formatRupees } from "@/lib/format";
import { hasPermission } from "@/lib/permissions";
import {
	type AdminPaymentDispute,
	type AdminPaymentRecord,
	cancelPaymentDisputeResolution,
	DISPUTE_RESOLUTION_OUTCOME_PERMISSIONS,
	type DisputeResolutionLegState,
	type DisputeResolutionOutcome,
	resolvePaymentDispute,
} from "../services/payments-api.service";

const OUTCOMES: readonly DisputeResolutionOutcome[] = [
	"release",
	"full_refund",
	"partial_refund",
];

const OUTCOME_LABELS: Record<DisputeResolutionOutcome, string> = {
	release: "Release",
	full_refund: "Full refund",
	partial_refund: "Split",
};

/**
 * The outcomes the signed-in operator can start, retry or cancel. The backend
 * charges `admin:payments:dispute:write` plus the permissions in
 * `DISPUTE_RESOLUTION_OUTCOME_PERMISSIONS` for each, so an outcome whose
 * permissions they lack is hidden rather than offered to be refused.
 */
export function usePermittedOutcomes(): DisputeResolutionOutcome[] {
	const operator = useAdminAuthStore((state) => state.operator);
	if (!hasPermission(operator, "admin:payments:dispute:write")) {
		return [];
	}
	return OUTCOMES.filter((outcome) =>
		DISPUTE_RESOLUTION_OUTCOME_PERMISSIONS[outcome].every((permission) =>
			hasPermission(operator, permission),
		),
	);
}

/**
 * A split's refund, read from what the operator typed. A split refunds a whole
 * number of rupees above zero and below the designer's share, as the backend
 * requires.
 */
function readSplitRefund(
	typed: string,
	share: number,
): { refund: number } | { error: string } | null {
	const trimmed = typed.trim();
	if (trimmed === "") {
		return null;
	}
	const refund = /^\d+$/.test(trimmed) ? Number(trimmed) : Number.NaN;
	if (!Number.isSafeInteger(refund) || refund <= 0) {
		return {
			error: "Enter the refund as a whole number of rupees above zero.",
		};
	}
	if (refund >= share) {
		return {
			error: `A split refunds less than the designer's share of ${formatRupees(share)}. To refund the whole payment, choose Full refund.`,
		};
	}
	return { refund };
}

/** What a resolution the backend answered with did, for the operator's toast. */
function describeResolved(dispute: AdminPaymentDispute): {
	tone: "success" | "warning";
	message: string;
} {
	const payout = formatRupees(dispute.resolutionPayoutAmount ?? 0);
	const refund = formatRupees(dispute.refundAmount ?? 0);
	switch (dispute.status) {
		case "resolved_release":
			return {
				tone: "success",
				message: `Resolved. The designer was paid ${payout}.`,
			};
		case "resolved_refund":
			return {
				tone: "success",
				message: `Resolved. The homeowner was refunded ${refund}.`,
			};
		case "resolved_partial":
			return {
				tone: "success",
				message: `Resolved. The designer was paid ${payout} and the homeowner was refunded ${refund}.`,
			};
	}
	if (dispute.resolutionRefundState === "pending") {
		const paid =
			dispute.resolutionPayoutState === "succeeded"
				? `The designer was paid ${payout}. `
				: "";
		return {
			tone: "warning",
			message: `${paid}Refund of ${refund} pending with the payment provider. Refund reconciliation finishes the resolution once the provider reports it processed for that amount.`,
		};
	}
	return {
		tone: "warning",
		message:
			"The resolution is still in progress. The dispute shows where each of its payments stands.",
	};
}

function toastResolved(dispute: AdminPaymentDispute): void {
	const { tone, message } = describeResolved(dispute);
	if (tone === "success") {
		toast.success(message);
	} else {
		toast.warning(message);
	}
}

const MIN_REASON_LENGTH = 10;

interface ResolveDisputePanelProps {
	payment: AdminPaymentRecord;
	dispute: AdminPaymentDispute;
	outcomes: DisputeResolutionOutcome[];
	/** Refetches the payment; run after the backend answers, either way. */
	onSettled: () => Promise<void>;
}

/**
 * Resolving an `open` or `under_review` dispute. Offers only `outcomes`, shows
 * the milestone's completion status beside them, the designer's part of a split
 * as the refund is typed, and the payout blockers of an outcome that pays the
 * designer, which keep it from being submitted.
 */
export function ResolveDisputePanel({
	payment,
	dispute,
	outcomes,
	onSettled,
}: ResolveDisputePanelProps) {
	const [outcome, setOutcome] = useState<DisputeResolutionOutcome>(outcomes[0]);
	const [typedRefund, setTypedRefund] = useState("");
	const [reason, setReason] = useState("");

	const resolve = useMutation({
		mutationFn: (input: {
			outcome: DisputeResolutionOutcome;
			reason: string;
			refundAmount?: number;
		}) => resolvePaymentDispute(dispute.id, input),
		onSuccess: async (result) => {
			toastResolved(result.dispute);
			setReason("");
			setTypedRefund("");
			await onSettled();
		},
		// `lib/api-client.ts` toasts the server's message. The refetch shows
		// where a leg that was refused now stands.
		onError: async () => {
			await onSettled();
		},
	});

	const share = payment.transaction.payeeAmount;
	const split =
		outcome === "partial_refund" ? readSplitRefund(typedRefund, share) : null;
	const paysDesigner = outcome !== "full_refund";
	const blocked = paysDesigner && !payment.payout.payoutReady;
	const milestoneStatus = payment.milestone.status;

	const canSubmit =
		reason.trim().length >= MIN_REASON_LENGTH &&
		!blocked &&
		(outcome !== "partial_refund" || (split !== null && "refund" in split));

	return (
		<div className="grid gap-3 rounded-2xl bg-slate-50 p-4">
			<p className="text-sm font-medium">Resolve dispute</p>
			{milestoneStatus ? (
				<p className="text-sm text-slate-700">
					Milestone:{" "}
					<span className="font-medium">
						{milestoneStatus === "completed" ? "Completed" : "Not completed"}
					</span>
					. A release or split does not need the milestone to be completed.
				</p>
			) : null}
			<fieldset className="flex flex-wrap gap-2">
				<legend className="sr-only">Outcome</legend>
				{outcomes.map((option) => (
					<Button
						key={option}
						type="button"
						size="sm"
						variant={option === outcome ? "default" : "outline"}
						aria-pressed={option === outcome}
						onClick={() => setOutcome(option)}
					>
						{OUTCOME_LABELS[option]}
					</Button>
				))}
			</fieldset>

			{outcome === "release" ? (
				<p className="text-sm text-slate-700">
					Pays the designer their share of {formatRupees(share)}.
				</p>
			) : null}
			{outcome === "full_refund" ? (
				<p className="text-sm text-slate-700">
					Refunds the homeowner the whole{" "}
					{formatRupees(payment.transaction.amount)} they paid.
				</p>
			) : null}
			{outcome === "partial_refund" ? (
				<div className="grid gap-2">
					<Input
						aria-label="Refund to the homeowner, in rupees"
						inputMode="numeric"
						value={typedRefund}
						onChange={(event) => setTypedRefund(event.target.value)}
						placeholder="Refund to the homeowner, in rupees"
					/>
					{split && "refund" in split ? (
						<p className="text-sm text-slate-700">
							The designer is paid {formatRupees(share - split.refund)} of their{" "}
							{formatRupees(share)} share, and the homeowner is refunded{" "}
							{formatRupees(split.refund)}.
						</p>
					) : null}
					{split && "error" in split ? (
						<p role="alert" className="text-sm text-red-700">
							{split.error}
						</p>
					) : null}
				</div>
			) : null}

			{blocked ? (
				<div role="alert" className="grid gap-1 text-sm text-amber-900">
					<p className="font-medium">
						The designer cannot be paid until this is fixed:
					</p>
					<ul className="grid gap-1">
						{payment.payout.blockers.map((blocker) => (
							<li key={blocker}>{blocker}</li>
						))}
					</ul>
				</div>
			) : null}

			<Textarea
				aria-label="Reason for this resolution"
				rows={4}
				value={reason}
				onChange={(event) => setReason(event.target.value)}
				placeholder="Explain why this outcome is correct."
			/>
			<Button
				onClick={() =>
					resolve.mutate({
						outcome,
						reason,
						refundAmount: split && "refund" in split ? split.refund : undefined,
					})
				}
				isLoading={resolve.isPending}
				disabled={!canSubmit}
			>
				Resolve dispute
			</Button>
		</div>
	);
}

const LEG_STATE_TEXT: Record<DisputeResolutionLegState, string> = {
	not_started: "Not sent to the payment provider yet.",
	pending: "Pending with the payment provider.",
	unconfirmed:
		"Unconfirmed: whether it went through could not be confirmed with the payment provider.",
	failed: "Failed.",
	succeeded: "Completed.",
};

/** The recorded intent of a resolving dispute, as a sentence. */
function describeIntent(dispute: AdminPaymentDispute): string {
	const payout = formatRupees(dispute.resolutionPayoutAmount ?? 0);
	const refund = formatRupees(dispute.refundAmount ?? 0);
	switch (dispute.resolutionOutcome) {
		case "release":
			return `Release: pays the designer ${payout}.`;
		case "full_refund":
			return `Full refund: refunds the homeowner ${refund}.`;
		case "partial_refund":
			return `Split: pays the designer ${payout} and refunds the homeowner ${refund}.`;
		default:
			return "The outcome of this resolution is not recorded.";
	}
}

interface ResolutionProgressProps {
	dispute: AdminPaymentDispute;
	outcomes: DisputeResolutionOutcome[];
	onSettled: () => Promise<void>;
}

/**
 * A `resolving` dispute: its outcome, amounts and where each leg stands.
 *
 * Retry sends the recorded outcome and amount again, which resumes it, and is
 * offered while no leg has failed and one is still to finish. A failed leg is
 * for support: retrying sends nothing and the backend refuses to cancel. Cancel
 * is offered only while no leg was ever sent to the payment provider, the one
 * case the backend cancels.
 */
export function ResolutionProgress({
	dispute,
	outcomes,
	onSettled,
}: ResolutionProgressProps) {
	const [reason, setReason] = useState("");
	const outcome = dispute.resolutionOutcome ?? null;
	const legs: Array<{ name: string; state: DisputeResolutionLegState }> = [];
	if (dispute.resolutionPayoutState) {
		legs.push({
			name: "Payout to the designer",
			state: dispute.resolutionPayoutState,
		});
	}
	if (dispute.resolutionRefundState) {
		legs.push({
			name: "Refund to the homeowner",
			state: dispute.resolutionRefundState,
		});
	}

	const failed = legs.filter((leg) => leg.state === "failed");
	const permitted = outcome !== null && outcomes.includes(outcome);
	const canRetry =
		permitted &&
		failed.length === 0 &&
		legs.some((leg) => leg.state !== "succeeded");
	const canCancel =
		permitted &&
		legs.length > 0 &&
		legs.every((leg) => leg.state === "not_started");
	const refundPending = dispute.resolutionRefundState === "pending";

	const retry = useMutation({
		mutationFn: () =>
			resolvePaymentDispute(dispute.id, {
				outcome: outcome as DisputeResolutionOutcome,
				reason,
				refundAmount:
					outcome === "partial_refund"
						? (dispute.refundAmount ?? undefined)
						: undefined,
			}),
		onSuccess: async (result) => {
			toastResolved(result.dispute);
			setReason("");
			await onSettled();
		},
		onError: async () => {
			await onSettled();
		},
	});

	const cancel = useMutation({
		mutationFn: () => cancelPaymentDisputeResolution(dispute.id, reason),
		onSuccess: async () => {
			toast.success("Resolution cancelled. The dispute is open again.");
			setReason("");
			await onSettled();
		},
		onError: async () => {
			await onSettled();
		},
	});

	const reasonReady = reason.trim().length >= MIN_REASON_LENGTH;
	const busy = retry.isPending || cancel.isPending;

	return (
		<div className="grid gap-3 rounded-2xl bg-slate-50 p-4">
			<p className="text-sm font-medium">Resolution in progress</p>
			<p className="text-sm text-slate-700">{describeIntent(dispute)}</p>
			<ul className="grid gap-1 text-sm text-slate-700">
				{legs.map((leg) => (
					<li key={leg.name}>
						<span className="font-medium">{leg.name}:</span>{" "}
						{LEG_STATE_TEXT[leg.state]}
					</li>
				))}
			</ul>
			{failed.length > 0 ? (
				<p role="alert" className="text-sm text-red-700">
					Support must handle the failed{" "}
					{failed.map((leg) => leg.name.toLowerCase()).join(" and ")}: retrying
					sends nothing, and this resolution cannot be cancelled.
				</p>
			) : null}
			{refundPending ? (
				<p className="text-sm text-slate-700">
					Refund reconciliation finishes this resolution once the payment
					provider reports the refund processed for the amount recorded.
				</p>
			) : null}
			{canRetry ? (
				<p className="text-sm text-slate-700">
					Retry sends the same outcome and amounts again. Each payout or refund
					is checked with the payment provider before it is sent, and one
					already completed is not sent again.
				</p>
			) : null}
			{canCancel ? (
				<p className="text-sm text-slate-700">
					Nothing has been sent to the payment provider yet, so this resolution
					can be cancelled and the dispute returns to open.
				</p>
			) : null}
			{canRetry || canCancel ? (
				<>
					<Textarea
						aria-label="Reason for retrying or cancelling"
						rows={3}
						value={reason}
						onChange={(event) => setReason(event.target.value)}
						placeholder="Explain why you are retrying or cancelling."
					/>
					<div className="flex flex-wrap gap-2">
						{canRetry ? (
							<Button
								onClick={() => retry.mutate()}
								isLoading={retry.isPending}
								disabled={!reasonReady || busy}
							>
								Retry resolution
							</Button>
						) : null}
						{canCancel ? (
							<Button
								variant="outline"
								onClick={() => cancel.mutate()}
								isLoading={cancel.isPending}
								disabled={!reasonReady || busy}
							>
								Cancel resolution
							</Button>
						) : null}
					</div>
				</>
			) : null}
		</div>
	);
}
